/**
 * Multi-select Site → Department → Unit cascade helpers (legacy parity):
 * hide unrelated options; prune invalid child selections when a parent changes.
 */

export interface OrgCascadeDepartmentOption {
  value: number
  siteId?: number | null
}

export interface OrgCascadeUnitOption {
  value: number
  siteId?: number | null
  departmentId?: number | null
}

export const visibleDepartmentOptionsForSites = <T extends OrgCascadeDepartmentOption>(
  departmentOptions: T[],
  siteIds: number[],
): T[] =>
  siteIds.length > 0
    ? departmentOptions.filter(
        (department) => department.siteId != null && siteIds.includes(department.siteId),
      )
    : departmentOptions

export const visibleUnitOptionsForParents = <T extends OrgCascadeUnitOption>(
  unitOptions: T[],
  siteIds: number[],
  departmentIds: number[],
): T[] =>
  unitOptions.filter((unit) => {
    if (siteIds.length > 0 && (unit.siteId == null || !siteIds.includes(unit.siteId))) {
      return false
    }
    if (
      departmentIds.length > 0 &&
      (unit.departmentId == null || !departmentIds.includes(unit.departmentId))
    ) {
      return false
    }
    return true
  })

export const pruneOrgCascadeSelections = ({
  departmentOptions,
  unitOptions,
  siteIds,
  departmentIds,
  unitIds,
}: {
  departmentOptions: OrgCascadeDepartmentOption[]
  unitOptions: OrgCascadeUnitOption[]
  siteIds: number[]
  departmentIds: number[]
  unitIds: number[]
}): { departmentIds: number[]; unitIds: number[] } => {
  const validDepartmentIds = departmentIds.filter((departmentId) => {
    const department = departmentOptions.find((option) => option.value === departmentId)
    // Drop unknown ids when a site filter is active so orphans cannot linger in the URL.
    if (!department) return siteIds.length === 0
    return (
      siteIds.length === 0 || (department.siteId != null && siteIds.includes(department.siteId))
    )
  })

  const validUnitIds = unitIds.filter((unitId) => {
    const unit = unitOptions.find((option) => option.value === unitId)
    if (!unit) return siteIds.length === 0 && validDepartmentIds.length === 0
    if (siteIds.length > 0 && (unit.siteId == null || !siteIds.includes(unit.siteId))) {
      return false
    }
    if (
      validDepartmentIds.length > 0 &&
      (unit.departmentId == null || !validDepartmentIds.includes(unit.departmentId))
    ) {
      return false
    }
    return true
  })

  return { departmentIds: validDepartmentIds, unitIds: validUnitIds }
}

export interface OrgCascadeSelection {
  siteIds: number[]
  departmentIds: number[]
  unitIds: number[]
}

const joinIdsOrUndefined = (ids: number[]): string | undefined =>
  ids.length > 0 ? ids.join(',') : undefined

/** Comma-separated report query params; empty selections become `undefined` (param removed). */
export const orgSelectionCsvParams = ({
  siteIds,
  departmentIds,
  unitIds,
}: OrgCascadeSelection): Record<
  'site_ids' | 'department_ids' | 'unit_ids',
  string | undefined
> => ({
  site_ids: joinIdsOrUndefined(siteIds),
  department_ids: joinIdsOrUndefined(departmentIds),
  unit_ids: joinIdsOrUndefined(unitIds),
})

export interface OrgCascadeChangeHandlers {
  changeSites: (siteIds: number[]) => OrgCascadeSelection
  changeDepartments: (departmentIds: number[]) => OrgCascadeSelection
  pruneSelection: () => OrgCascadeSelection
}

/**
 * Wire `changeSites` / `changeDepartments` to MultiSelect `onChange` (and reuse them in
 * `onClose`) so invalid children are dropped on every toggle — not deferred until the
 * dropdown or mobile sheet closes, where orphans would still read "N selected".
 */
export const buildOrgCascadeChangeHandlers = ({
  departmentOptions,
  unitOptions,
  selection,
  applySelection,
}: {
  departmentOptions: OrgCascadeDepartmentOption[]
  unitOptions: OrgCascadeUnitOption[]
  selection: OrgCascadeSelection
  applySelection: (selection: OrgCascadeSelection) => void
}): OrgCascadeChangeHandlers => {
  const applyPrunedSelection = (siteIds: number[], departmentIds: number[]) => {
    const prunedSelection = {
      siteIds,
      ...pruneOrgCascadeSelections({
        departmentOptions,
        unitOptions,
        siteIds,
        departmentIds,
        unitIds: selection.unitIds,
      }),
    }
    applySelection(prunedSelection)
    return prunedSelection
  }

  return {
    changeSites: (siteIds) => applyPrunedSelection(siteIds, selection.departmentIds),
    changeDepartments: (departmentIds) => applyPrunedSelection(selection.siteIds, departmentIds),
    pruneSelection: () => applyPrunedSelection(selection.siteIds, selection.departmentIds),
  }
}
